import { useState } from "react";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Loader2 } from "lucide-react";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
const BACKEND_URL = "" || "";

/**
 * Lado interno do componente — corre dentro do PayPalScriptProvider e por isso
 * tem acesso ao `usePayPalScriptReducer` para detectar quando o SDK
 * `window.paypal` está pronto. Renderiza:
 *   • PayPal default Smart Button (amarelo) + Cartão (preto) + Pay Later (se elegível)
 *   • Apple Pay button dedicado (apenas se elegível: Safari/iOS + merchant config)
 *
 * NOTE: `enable-funding=applepay` no SDK options FAZ aparecer Apple Pay
 * automaticamente nos Smart Buttons em Safari, mas adicionar um botão
 * dedicado dá ao utilizador um CTA visualmente distintivo (botão preto Apple)
 * que é mais reconhecível e converte melhor.
 */
function PaypalButtonsInner({ orderId, hashEncomenda, total, currency, onCapture, status }) {
  const [{ isResolved }] = usePayPalScriptReducer();
  const cur = (currency || "EUR").toUpperCase();
  const valueStr = Number(total).toFixed(2);

  // Verifica eligibility do Apple Pay (Safari/iOS + merchant activo + domain
  // verification). `window.paypal.FUNDING.APPLEPAY` é exposto pelo SDK.
  const applePayEligible = (() => {
    if (!isResolved || typeof window === "undefined" || !window.paypal) return false;
    try {
      const FUNDING = window.paypal.FUNDING;
      if (!FUNDING || !FUNDING.APPLEPAY) return false;
      const btn = window.paypal.Buttons({ fundingSource: FUNDING.APPLEPAY });
      return typeof btn.isEligible === "function" && btn.isEligible();
    } catch {
      return false;
    }
  })();

  const buildOrder = (_, actions) =>
    actions.order.create({
      intent: "CAPTURE",
      purchase_units: [{
        reference_id: hashEncomenda || String(orderId),
        amount: { currency_code: cur, value: valueStr },
        description: `Encomenda IHTP #${orderId}`,
      }],
    });

  const handleApprove = async (data, actions) => {
    await actions.order.capture();
    await onCapture(data.orderID);
  };

  return (
    <>
      {/* Default Smart Buttons: PayPal + Cartão + Pay Later (se elegível por país) */}
      <PayPalButtons
        style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
        disabled={status === "confirming"}
        createOrder={buildOrder}
        onApprove={handleApprove}
        onCancel={() => onCapture(null, "cancelled")}
        onError={(err) => onCapture(null, err?.message || "Erro PayPal")}
      />

      {/* Apple Pay dedicado — só renderiza se eligible (Safari/iOS + merchant config) */}
      {applePayEligible && (
        <div className="mt-2" data-testid="paypal-applepay-wrapper">
          <PayPalButtons
            fundingSource="applepay"
            style={{ layout: "vertical", color: "black", shape: "rect", label: "checkout", height: 45 }}
            disabled={status === "confirming"}
            createOrder={buildOrder}
            onApprove={handleApprove}
            onCancel={() => onCapture(null, "cancelled")}
            onError={(err) => onCapture(null, err?.message || "Erro Apple Pay")}
          />
        </div>
      )}
    </>
  );
}

/**
 * MyOrderPaymentPaypal — substitui o approach legacy "abrir UrlPagamentoPP".
 * Usa @paypal/react-paypal-js Smart Buttons:
 *   • createOrder → PayPal SDK directly (sem o nosso backend)
 *   • onApprove   → captura no PayPal + POST /api/my/shop/payment/paypal-confirm
 */
export default function MyOrderPaymentPaypal({
  orderId, hashEncomenda, total, currency, onPaid,
}) {
  const [status, setStatus] = useState("idle"); // idle | confirming | done | error
  const [error, setError] = useState("");

  if (!PAYPAL_CLIENT_ID) {
    return (
      <div className="text-xs text-[#991B1B] p-3 bg-[#FEF2F2] rounded-md">
        Configuração PayPal em falta. Contacte o administrador.
      </div>
    );
  }

  const handleCapture = async (paypalOrderId, errMsg) => {
    if (errMsg) {
      setStatus(errMsg === "cancelled" ? "idle" : "error");
      setError(errMsg === "cancelled" ? "Pagamento cancelado." : `Erro: ${errMsg}`);
      return;
    }
    setStatus("confirming");
    setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/my/shop/payment/paypal-confirm`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ID_Encomenda: orderId, HashEncomenda: hashEncomenda, PaypalOrderId: paypalOrderId }),
      });
      const data = await r.json();
      if (!data.success) throw new Error(data.message || "Falha a confirmar pagamento");
      setStatus("done");
      if (onPaid) onPaid(data);
    } catch (e) {
      setStatus("error");
      setError(e.message || "Erro inesperado.");
    }
  };

  if (status === "done") {
    return (
      <div className="text-sm text-center p-6 bg-[#ECFDF5] rounded-md text-[#065F46]" data-testid="paypal-confirmed">
        ✅ Pagamento confirmado. A sua encomenda foi actualizada.
      </div>
    );
  }

  return (
    <div data-testid="paypal-button-wrapper">
      {status === "confirming" && (
        <div className="text-xs text-[#6B7280] mb-2 text-center">
          <Loader2 size={14} className="inline animate-spin mr-1" /> A confirmar com o IHTP…
        </div>
      )}
      {error && (
        <div className="text-xs text-[#991B1B] mb-2 p-2 bg-[#FEF2F2] rounded" data-testid="paypal-error">
          {error}
        </div>
      )}
      <PayPalScriptProvider
        options={{
          clientId: PAYPAL_CLIENT_ID,
          currency: currency || "EUR",
          locale: "pt_PT",
          components: "buttons",
          // Apple Pay aparece em Safari/iOS + merchant ApplePay activo +
          // Domain Verification feita. Pay Later parcelado em regiões
          // elegíveis. Venmo só US (auto-ignored fora).
          "enable-funding": "applepay,paylater,venmo",
        }}
      >
        <PaypalButtonsInner
          orderId={orderId}
          hashEncomenda={hashEncomenda}
          total={total}
          currency={currency}
          onCapture={handleCapture}
          status={status}
        />
      </PayPalScriptProvider>
    </div>
  );
}
